import type {
  AccessSetting,
  AuthorProfile,
  ChannelAccessGrant,
  ChannelKind,
  ChatMessage,
  CommunityChat,
  CommunityForum,
  CommunityForumState,
  CommunitySidebarHeader,
  CommunityTopic,
  SectionStatus,
  SectionType,
  TopicReply,
  TopicStatus,
  TopicSummary,
  ViewAccessSetting,
} from './types';

const STORAGE_KEY = 'dc_community_forum_v2';
const LEGACY_KEY = 'dc_community_forum_v1';

export const FORUM_STORAGE_KEY = STORAGE_KEY;
export const FORUM_LEGACY_STORAGE_KEY = LEGACY_KEY;

const DEFAULT_HEADER_ID = 'hdr_main';

function uid(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function daysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function hoursAgo(hours: number) {
  const d = new Date();
  d.setHours(d.getHours() - hours);
  return d.toISOString();
}

function defaultHeader(ts: string): CommunitySidebarHeader {
  return {
    id: DEFAULT_HEADER_ID,
    title: 'Разделы',
    sortOrder: 1,
    status: 'active',
    createdAt: ts,
    updatedAt: ts,
  };
}

function buildSeedState(): CommunityForumState {
  const ts = daysAgo(90);
  const hdrMain = DEFAULT_HEADER_ID;
  const hdrService = 'hdr_service';

  const headers: CommunitySidebarHeader[] = [
    defaultHeader(ts),
    {
      id: hdrService,
      title: 'Служебное',
      sortOrder: 2,
      status: 'active',
      createdAt: daysAgo(60),
      updatedAt: daysAgo(5),
    },
  ];

  const chatGeneral = 'chat_general';

  const forums: CommunityForum[] = [
    {
      id: 'sec_announcements',
      headerId: hdrMain,
      name: 'Объявления datacircle',
      description: 'Официальные обновления платформы, правила и важные уведомления для участников.',
      icon: 'fa-bullhorn',
      type: 'announcements',
      sortOrder: 1,
      status: 'active',
      createTopicsAccess: 'admins',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(90),
      updatedAt: daysAgo(2),
    },
    {
      id: 'sec_compensation',
      headerId: hdrMain,
      name: 'Компенсация',
      description: 'Обсуждение фиксированной и переменной части, бонусов и рыночных ориентиров по компенсации.',
      icon: 'fa-coins',
      type: 'regular',
      sortOrder: 2,
      status: 'active',
      createTopicsAccess: 'all',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(80),
      updatedAt: daysAgo(1),
    },
    {
      id: 'sec_roles',
      headerId: hdrMain,
      name: 'Уровни ролей',
      description: 'Сопоставление должностей, фактической зоны ответственности и уровня роли на рынке.',
      icon: 'fa-layer-group',
      type: 'regular',
      sortOrder: 3,
      status: 'active',
      createTopicsAccess: 'all',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(70),
      updatedAt: daysAgo(3),
    },
    {
      id: 'sec_ltip',
      headerId: hdrMain,
      name: 'Долгосрочная мотивация',
      description: 'Участие в прибыли, carry, опционы и другие формы долгосрочного вознаграждения.',
      icon: 'fa-chart-line',
      type: 'regular',
      sortOrder: 4,
      status: 'active',
      createTopicsAccess: 'all',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(60),
      updatedAt: daysAgo(5),
    },
    {
      id: 'sec_career',
      headerId: hdrMain,
      name: 'Карьерные переходы',
      description: 'Переходы между секторами, офферы, переговоры и смена траектории в инвестиционном рынке.',
      icon: 'fa-route',
      type: 'regular',
      sortOrder: 5,
      status: 'active',
      createTopicsAccess: 'all',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(50),
      updatedAt: daysAgo(4),
    },
    {
      id: 'sec_qa',
      headerId: hdrMain,
      name: 'Вопросы к участникам',
      description: 'Открытые вопросы к проверенным участникам сообщества по рынку и практике.',
      icon: 'fa-circle-question',
      type: 'regular',
      sortOrder: 6,
      status: 'active',
      createTopicsAccess: 'all',
      replyAccess: 'all',
      viewAccess: 'all',
      createdAt: daysAgo(40),
      updatedAt: daysAgo(6),
    },
  ];

  const chats: CommunityChat[] = [
    {
      id: chatGeneral,
      headerId: hdrService,
      name: 'Общий чат',
      description: 'Свободный текстовый чат для быстрых вопросов и обмена контекстом.',
      icon: 'fa-comments',
      sortOrder: 1,
      status: 'active',
      postAccess: 'all',
      viewAccess: 'invite',
      createdAt: daysAgo(30),
      updatedAt: hoursAgo(2),
    },
  ];

  const t1 = 'topic_1';
  const t2 = 'topic_2';
  const t3 = 'topic_3';

  const topics: CommunityTopic[] = [
    {
      id: t1,
      sectionId: 'sec_compensation',
      title: 'Какой краткосрочный бонус считается рыночным для инвестиционного директора?',
      description:
        'Рассматриваю оффер в фонд прямых инвестиций среднего размера. Хочу понять, какой диапазон краткосрочного бонуса считается рыночным для роли уровня директора с участием в сделках, но без финального слова по инвестиционному решению.',
      tags: ['Прямые инвестиции', 'Директор', 'Краткосрочный бонус'],
      author: {
        pseudonym: 'Анонимный участник',
        industry: 'Инвестиции',
        sector: 'Прямые инвестиции',
        roleLevel: 'Директор',
        isAnonymous: true,
      },
      status: 'pinned',
      hasSummary: true,
      summary: {
        body:
          'Участники сходятся в том, что краткосрочный бонус для роли уровня директора сильно зависит от размера фонда, участия в сделках и фактической зоны ответственности. Один только title недостаточен для корректного сравнения.',
        preparedBy: 'datacircle',
        updatedAt: daysAgo(1),
      },
      replyCount: 12,
      lastActivityAt: hoursAgo(3),
      createdAt: daysAgo(14),
    },
    {
      id: t2,
      sectionId: 'sec_roles',
      title: 'Когда вице-президент фактически выполняет задачи уровня директора?',
      description:
        'В текущей роли VP закрываю сделки end-to-end, веду переговоры с клиентами и формирую команду. Интересно, как участники рынка отличают «title VP» от фактического уровня директора при оценке компенсации.',
      tags: ['Инвестбанкинг', 'Вице-президент', 'Уровень роли'],
      author: {
        pseudonym: 'Северный мандат',
        industry: 'Финансовые услуги',
        sector: 'Инвестиционный банкинг',
        roleLevel: 'Вице-президент',
        experienceRange: '7–10 лет',
      },
      status: 'open',
      hasSummary: false,
      replyCount: 8,
      lastActivityAt: hoursAgo(8),
      createdAt: daysAgo(10),
    },
    {
      id: t3,
      sectionId: 'sec_ltip',
      title: 'Как оценивать участие в прибыли фонда для принципала?',
      description:
        'Получил предложение с базовым carry и ограниченным участием в economics фонда. Какие параметры стоит уточнить, чтобы понять, насколько предложение соответствует рынку для принципала?',
      tags: ['Венчурные фонды', 'Партнер', 'Долгосрочная мотивация'],
      author: {
        pseudonym: 'Анонимный участник',
        industry: 'Инвестиции',
        sector: 'Венчурные фонды',
        roleLevel: 'Партнер',
        isAnonymous: true,
      },
      status: 'open',
      hasSummary: false,
      replyCount: 6,
      lastActivityAt: daysAgo(2),
      createdAt: daysAgo(7),
    },
  ];

  const replies: TopicReply[] = [
    {
      id: 'reply_1',
      topicId: t1,
      body: 'На мой взгляд, важно смотреть не только на процент бонуса, но и на формулу: привязка к сделкам, personal P&L и размер команды существенно меняют картину.',
      author: {
        pseudonym: 'Северный мандат',
        industry: 'Инвестиции',
        sector: 'Прямые инвестиции',
        roleLevel: 'Директор',
        experienceRange: '10+ лет',
      },
      createdAt: daysAgo(12),
    },
    {
      id: 'reply_2',
      topicId: t1,
      body: 'Согласен с акцентом на зону ответственности. В фондах одного масштаба диапазон может отличаться в 1,5–2 раза при схожем title.',
      author: {
        pseudonym: 'Анонимный участник',
        industry: 'Инвестиции',
        sector: 'Прямые инвестиции',
        roleLevel: 'Партнер',
        isAnonymous: true,
      },
      createdAt: daysAgo(11),
    },
    {
      id: 'reply_3',
      topicId: t2,
      body: 'Обычно смотрят на три признака: размер сделок, самостоятельность в переговорах и влияние на staffing. Если все три на уровне директора — title часто отстаёт от фактической роли.',
      author: {
        pseudonym: 'Анонимный участник',
        industry: 'Финансовые услуги',
        sector: 'Инвестиционный банкинг',
        roleLevel: 'Директор',
        isAnonymous: true,
      },
      createdAt: daysAgo(9),
    },
  ];

  const chatMessages: ChatMessage[] = [
    {
      id: 'cmsg_1',
      chatId: chatGeneral,
      body: 'Добро пожаловать в общий чат. Здесь можно быстро уточнить контекст по рынку без создания отдельной темы.',
      author: {
        pseudonym: 'datacircle',
        sector: 'Платформа',
        roleLevel: 'Модератор',
      },
      createdAt: daysAgo(5),
    },
    {
      id: 'cmsg_2',
      chatId: chatGeneral,
      body: 'Есть ли у кого-то свежие ориентиры по STI в mid-market PE на уровне VP?',
      author: {
        pseudonym: 'Анонимный участник',
        industry: 'Инвестиции',
        sector: 'Прямые инвестиции',
        roleLevel: 'Вице-президент',
        isAnonymous: true,
      },
      createdAt: hoursAgo(5),
    },
    {
      id: 'cmsg_3',
      chatId: chatGeneral,
      body: 'Вижу разброс 15–35% от FIX в зависимости от фонда и персонального track record.',
      author: {
        pseudonym: 'Северный мандат',
        industry: 'Инвестиции',
        sector: 'Прямые инвестиции',
        roleLevel: 'Директор',
      },
      createdAt: hoursAgo(2),
    },
  ];

  return { headers, forums, chats, chatMessages, topics, replies, accessGrants: [] };
}

function withCounts(state: CommunityForumState): CommunityForumState {
  const topicCounts = new Map<string, number>();
  state.topics.forEach((t) => {
    if (t.status !== 'hidden') {
      topicCounts.set(t.sectionId, (topicCounts.get(t.sectionId) || 0) + 1);
    }
  });

  const msgMeta = new Map<string, { count: number; last: string }>();
  state.chatMessages.forEach((m) => {
    const cur = msgMeta.get(m.chatId);
    if (!cur) {
      msgMeta.set(m.chatId, { count: 1, last: m.createdAt });
    } else {
      cur.count += 1;
      if (m.createdAt > cur.last) cur.last = m.createdAt;
    }
  });

  return {
    ...state,
    forums: state.forums.map((f) => ({
      ...f,
      topicCount: topicCounts.get(f.id) || 0,
    })),
    chats: state.chats.map((c) => {
      const meta = msgMeta.get(c.id);
      return {
        ...c,
        messageCount: meta?.count ?? 0,
        lastActivityAt: meta?.last ?? c.updatedAt,
      };
    }),
  };
}

function migrateLegacy(parsed: Record<string, unknown>): CommunityForumState {
  const ts = nowIso();
  const header = defaultHeader(ts);
  const legacySections = (parsed.sections as CommunityForum[] | undefined) ?? [];
  const forums = legacySections.map((s) => ({
    ...s,
    headerId: (s as CommunityForum).headerId ?? header.id,
  }));
  return withCounts({
    headers: [header],
    forums,
    chats: (parsed.chats as CommunityChat[] | undefined) ?? [],
    chatMessages: (parsed.chatMessages as ChatMessage[] | undefined) ?? [],
    topics: (parsed.topics as CommunityTopic[] | undefined) ?? [],
    replies: (parsed.replies as TopicReply[] | undefined) ?? [],
    accessGrants: [],
  });
}

function normalizeViewAccess(value: unknown): ViewAccessSetting {
  return value === 'invite' ? 'invite' : 'all';
}

function normalizeState(raw: CommunityForumState): CommunityForumState {
  let headers = raw.headers ?? [];
  let forums = raw.forums ?? (raw as { sections?: CommunityForum[] }).sections ?? [];
  if (!headers.length) {
    headers = [defaultHeader(nowIso())];
    forums = forums.map((f) => ({ ...f, headerId: f.headerId || DEFAULT_HEADER_ID }));
  }
  return withCounts({
    headers,
    forums: forums.map((f) => ({ ...f, viewAccess: normalizeViewAccess(f.viewAccess) })),
    chats: (raw.chats ?? []).map((c) => ({ ...c, viewAccess: normalizeViewAccess(c.viewAccess) })),
    chatMessages: raw.chatMessages ?? [],
    topics: raw.topics ?? [],
    replies: raw.replies ?? [],
    accessGrants: Array.isArray(raw.accessGrants) ? raw.accessGrants : [],
  });
}

export function loadForumState(): CommunityForumState {
  try {
    const rawV2 = localStorage.getItem(STORAGE_KEY);
    if (rawV2) {
      return normalizeState(JSON.parse(rawV2) as CommunityForumState);
    }
    const rawV1 = localStorage.getItem(LEGACY_KEY);
    if (rawV1) {
      const migrated = migrateLegacy(JSON.parse(rawV1) as Record<string, unknown>);
      saveForumState(migrated);
      return migrated;
    }
    const seed = withCounts(buildSeedState());
    saveForumState(seed);
    return seed;
  } catch {
    const seed = withCounts(buildSeedState());
    saveForumState(seed);
    return seed;
  }
}

export function saveForumState(state: CommunityForumState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(withCounts(state)));
}

export type ForumStorageStats = {
  storageKey: string;
  legacyKey: string;
  payloadBytes: number;
  hasLegacyCopy: boolean;
  counts: {
    headers: number;
    forums: number;
    chats: number;
    chatMessages: number;
    topics: number;
    replies: number;
  };
};

export function getForumStorageStats(state: CommunityForumState): ForumStorageStats {
  let payloadBytes = 0;
  let hasLegacyCopy = false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    payloadBytes = raw ? new Blob([raw]).size : 0;
    hasLegacyCopy = localStorage.getItem(LEGACY_KEY) != null;
  } catch {
    /* ignore */
  }
  return {
    storageKey: STORAGE_KEY,
    legacyKey: LEGACY_KEY,
    payloadBytes,
    hasLegacyCopy,
    counts: {
      headers: state.headers.length,
      forums: state.forums.length,
      chats: state.chats.length,
      chatMessages: state.chatMessages.length,
      topics: state.topics.length,
      replies: state.replies.length,
    },
  };
}

export function getActiveHeaders(state: CommunityForumState): CommunitySidebarHeader[] {
  return [...state.headers]
    .filter((h) => h.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getActiveForums(state: CommunityForumState): CommunityForum[] {
  return [...state.forums]
    .filter((f) => f.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getForumsForHeader(state: CommunityForumState, headerId: string): CommunityForum[] {
  return state.forums
    .filter((f) => f.headerId === headerId && f.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getChatsForHeader(state: CommunityForumState, headerId: string): CommunityChat[] {
  return state.chats
    .filter((c) => c.headerId === headerId && c.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function normalizeMemberEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function canViewChannel(
  state: CommunityForumState,
  kind: ChannelKind,
  channelId: string,
  viewerEmail: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  const channel =
    kind === 'chat'
      ? state.chats.find((c) => c.id === channelId)
      : state.forums.find((f) => f.id === channelId);
  if (!channel || channel.status !== 'active') return false;
  if (normalizeViewAccess(channel.viewAccess) === 'all') return true;
  if (!viewerEmail) return false;
  const email = normalizeMemberEmail(viewerEmail);
  return (state.accessGrants ?? []).some(
    (g) => g.channelKind === kind && g.channelId === channelId && g.userEmail === email,
  );
}

export function getVisibleForumsForHeader(
  state: CommunityForumState,
  headerId: string,
  viewerEmail: string | null | undefined,
  isAdmin: boolean,
): CommunityForum[] {
  return getForumsForHeader(state, headerId).filter((f) =>
    canViewChannel(state, 'forum', f.id, viewerEmail, isAdmin),
  );
}

export function getVisibleChatsForHeader(
  state: CommunityForumState,
  headerId: string,
  viewerEmail: string | null | undefined,
  isAdmin: boolean,
): CommunityChat[] {
  return getChatsForHeader(state, headerId).filter((c) =>
    canViewChannel(state, 'chat', c.id, viewerEmail, isAdmin),
  );
}

export function getChannelGrants(
  state: CommunityForumState,
  kind: ChannelKind,
  channelId: string,
): ChannelAccessGrant[] {
  return (state.accessGrants ?? [])
    .filter((g) => g.channelKind === kind && g.channelId === channelId)
    .sort((a, b) => a.userLabel.localeCompare(b.userLabel, 'ru'));
}

export function grantChannelAccess(
  state: CommunityForumState,
  kind: ChannelKind,
  channelId: string,
  userEmail: string,
  userLabel: string,
  userId?: string,
): CommunityForumState {
  const email = normalizeMemberEmail(userEmail);
  if (!email) return state;
  const grants = state.accessGrants ?? [];
  const existing = grants.find(
    (g) => g.channelKind === kind && g.channelId === channelId && g.userEmail === email,
  );
  if (existing) {
    return withCounts({
      ...state,
      accessGrants: grants.map((g) =>
        g.id === existing.id
          ? {
              ...g,
              userLabel: userLabel.trim() || email,
              userId: userId || g.userId,
              grantedAt: nowIso(),
            }
          : g,
      ),
    });
  }
  const grant: ChannelAccessGrant = {
    id: uid('grant'),
    channelKind: kind,
    channelId,
    userId,
    userEmail: email,
    userLabel: userLabel.trim() || email,
    grantedAt: nowIso(),
  };
  return withCounts({ ...state, accessGrants: [...grants, grant] });
}

export function revokeChannelAccessByEmail(
  state: CommunityForumState,
  kind: ChannelKind,
  channelId: string,
  userEmail: string,
): CommunityForumState {
  const email = normalizeMemberEmail(userEmail);
  return withCounts({
    ...state,
    accessGrants: (state.accessGrants ?? []).filter(
      (g) => !(g.channelKind === kind && g.channelId === channelId && g.userEmail === email),
    ),
  });
}

export function revokeChannelAccess(state: CommunityForumState, grantId: string): CommunityForumState {
  return withCounts({
    ...state,
    accessGrants: (state.accessGrants ?? []).filter((g) => g.id !== grantId),
  });
}

function removeGrantsForChannel(state: CommunityForumState, kind: ChannelKind, channelId: string) {
  return (state.accessGrants ?? []).filter(
    (g) => !(g.channelKind === kind && g.channelId === channelId),
  );
}

/** @deprecated use getActiveForums */
export function getActiveSections(state: CommunityForumState): CommunityForum[] {
  return getActiveForums(state);
}

export function getTopicsForSection(state: CommunityForumState, sectionId: string): CommunityTopic[] {
  return state.topics
    .filter((t) => t.sectionId === sectionId && t.status !== 'hidden')
    .sort((a, b) => {
      if (a.status === 'pinned' && b.status !== 'pinned') return -1;
      if (b.status === 'pinned' && a.status !== 'pinned') return 1;
      return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
    });
}

export type HeaderInput = {
  title: string;
  sortOrder: number;
  status: SectionStatus;
};

export function createHeader(state: CommunityForumState, input: HeaderInput): CommunityForumState {
  const ts = nowIso();
  const header: CommunitySidebarHeader = { id: uid('hdr'), ...input, createdAt: ts, updatedAt: ts };
  return withCounts({ ...state, headers: [...state.headers, header] });
}

export function updateHeader(
  state: CommunityForumState,
  id: string,
  input: Partial<HeaderInput>,
): CommunityForumState {
  return withCounts({
    ...state,
    headers: state.headers.map((h) =>
      h.id === id ? { ...h, ...input, updatedAt: nowIso() } : h,
    ),
  });
}

export function deleteHeader(state: CommunityForumState, id: string): CommunityForumState {
  const hasChildren =
    state.forums.some((f) => f.headerId === id)
    || state.chats.some((c) => c.headerId === id);
  if (hasChildren) {
    throw new Error('Нельзя удалить заголовок: под ним есть чаты или форумы');
  }
  return withCounts({
    ...state,
    headers: state.headers.filter((h) => h.id !== id),
  });
}

export function reorderHeaders(state: CommunityForumState, orderedIds: string[]): CommunityForumState {
  const orderMap = new Map(orderedIds.map((id, i) => [id, i + 1]));
  return withCounts({
    ...state,
    headers: state.headers.map((h) => ({
      ...h,
      sortOrder: orderMap.get(h.id) ?? h.sortOrder,
      updatedAt: nowIso(),
    })),
  });
}

export type ForumInput = {
  headerId: string;
  name: string;
  description: string;
  icon: string;
  type: SectionType;
  sortOrder: number;
  status: SectionStatus;
  createTopicsAccess: AccessSetting;
  replyAccess: AccessSetting;
  viewAccess: ViewAccessSetting;
};

/** @deprecated */
export type SectionInput = ForumInput;

export function createForum(state: CommunityForumState, input: ForumInput): CommunityForumState {
  const ts = nowIso();
  const forum: CommunityForum = { id: uid('sec'), ...input, createdAt: ts, updatedAt: ts };
  return withCounts({ ...state, forums: [...state.forums, forum] });
}

/** @deprecated */
export const createSection = createForum;

export function updateForum(
  state: CommunityForumState,
  id: string,
  input: Partial<ForumInput>,
): CommunityForumState {
  return withCounts({
    ...state,
    forums: state.forums.map((f) =>
      f.id === id ? { ...f, ...input, updatedAt: nowIso() } : f,
    ),
  });
}

/** @deprecated */
export const updateSection = updateForum;

export function deleteForum(state: CommunityForumState, id: string): CommunityForumState {
  const topicIds = new Set(state.topics.filter((t) => t.sectionId === id).map((t) => t.id));
  return withCounts({
    ...state,
    forums: state.forums.filter((f) => f.id !== id),
    topics: state.topics.filter((t) => t.sectionId !== id),
    replies: state.replies.filter((r) => !topicIds.has(r.topicId)),
    accessGrants: removeGrantsForChannel(state, 'forum', id),
  });
}

/** @deprecated */
export const deleteSection = deleteForum;

export function reorderForums(state: CommunityForumState, orderedIds: string[]): CommunityForumState {
  const orderMap = new Map(orderedIds.map((id, i) => [id, i + 1]));
  return withCounts({
    ...state,
    forums: state.forums.map((f) => ({
      ...f,
      sortOrder: orderMap.get(f.id) ?? f.sortOrder,
      updatedAt: nowIso(),
    })),
  });
}

/** @deprecated */
export const reorderSections = reorderForums;

export type ChatInput = {
  headerId: string;
  name: string;
  description: string;
  icon: string;
  sortOrder: number;
  status: SectionStatus;
  postAccess: AccessSetting;
  viewAccess: ViewAccessSetting;
};

export function createChat(state: CommunityForumState, input: ChatInput): CommunityForumState {
  const ts = nowIso();
  const chat: CommunityChat = { id: uid('chat'), ...input, createdAt: ts, updatedAt: ts };
  return withCounts({ ...state, chats: [...state.chats, chat] });
}

export function updateChat(
  state: CommunityForumState,
  id: string,
  input: Partial<ChatInput>,
): CommunityForumState {
  return withCounts({
    ...state,
    chats: state.chats.map((c) =>
      c.id === id ? { ...c, ...input, updatedAt: nowIso() } : c,
    ),
  });
}

export function deleteChat(state: CommunityForumState, id: string): CommunityForumState {
  return withCounts({
    ...state,
    chats: state.chats.filter((c) => c.id !== id),
    chatMessages: state.chatMessages.filter((m) => m.chatId !== id),
    accessGrants: removeGrantsForChannel(state, 'chat', id),
  });
}

export function reorderChats(state: CommunityForumState, orderedIds: string[]): CommunityForumState {
  const orderMap = new Map(orderedIds.map((id, i) => [id, i + 1]));
  return withCounts({
    ...state,
    chats: state.chats.map((c) => ({
      ...c,
      sortOrder: orderMap.get(c.id) ?? c.sortOrder,
      updatedAt: nowIso(),
    })),
  });
}

export function getChatMessages(state: CommunityForumState, chatId: string): ChatMessage[] {
  return state.chatMessages
    .filter((m) => m.chatId === chatId)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export function createChatMessage(
  state: CommunityForumState,
  chatId: string,
  body: string,
  author: AuthorProfile,
  authorKey?: string | null,
): CommunityForumState {
  const ts = nowIso();
  const message: ChatMessage = {
    id: uid('cmsg'),
    chatId,
    body: body.trim(),
    author,
    authorKey: authorKey?.trim().toLowerCase() || undefined,
    createdAt: ts,
  };
  return withCounts({
    ...state,
    chatMessages: [...state.chatMessages, message],
    chats: state.chats.map((c) =>
      c.id === chatId ? { ...c, updatedAt: ts } : c,
    ),
  });
}

export function updateChatMessage(
  state: CommunityForumState,
  messageId: string,
  body: string,
): CommunityForumState {
  const ts = nowIso();
  return withCounts({
    ...state,
    chatMessages: state.chatMessages.map((m) =>
      m.id === messageId ? { ...m, body: body.trim(), updatedAt: ts } : m,
    ),
  });
}

export function deleteChatMessage(state: CommunityForumState, messageId: string): CommunityForumState {
  return withCounts({
    ...state,
    chatMessages: state.chatMessages.filter((m) => m.id !== messageId),
  });
}

export type TopicInput = {
  sectionId: string;
  title: string;
  description: string;
  tags: string[];
  author: CommunityTopic['author'];
};

export function createTopic(state: CommunityForumState, input: TopicInput): CommunityForumState {
  const ts = nowIso();
  const topic: CommunityTopic = {
    id: uid('topic'),
    sectionId: input.sectionId,
    title: input.title.trim(),
    description: input.description.trim(),
    tags: input.tags,
    author: input.author,
    status: 'open',
    hasSummary: false,
    replyCount: 0,
    lastActivityAt: ts,
    createdAt: ts,
  };
  return withCounts({ ...state, topics: [topic, ...state.topics] });
}

export function createReply(
  state: CommunityForumState,
  topicId: string,
  body: string,
  author: TopicReply['author'],
  authorKey?: string | null,
): CommunityForumState {
  const ts = nowIso();
  const reply: TopicReply = {
    id: uid('reply'),
    topicId,
    body: body.trim(),
    author,
    authorKey: authorKey?.trim().toLowerCase() || undefined,
    createdAt: ts,
  };
  return withCounts({
    ...state,
    replies: [...state.replies, reply],
    topics: state.topics.map((t) =>
      t.id === topicId
        ? { ...t, replyCount: t.replyCount + 1, lastActivityAt: ts }
        : t,
    ),
  });
}

export function updateTopicStatus(
  state: CommunityForumState,
  topicId: string,
  status: TopicStatus,
): CommunityForumState {
  return withCounts({
    ...state,
    topics: state.topics.map((t) => (t.id === topicId ? { ...t, status } : t)),
  });
}

export function setTopicSummary(
  state: CommunityForumState,
  topicId: string,
  summary: Omit<TopicSummary, 'updatedAt'>,
): CommunityForumState {
  return withCounts({
    ...state,
    topics: state.topics.map((t) =>
      t.id === topicId
        ? {
            ...t,
            hasSummary: true,
            summary: { ...summary, updatedAt: nowIso() },
          }
        : t,
    ),
  });
}

export function deleteTopic(state: CommunityForumState, topicId: string): CommunityForumState {
  return withCounts({
    ...state,
    topics: state.topics.filter((t) => t.id !== topicId),
    replies: state.replies.filter((r) => r.topicId !== topicId),
  });
}

export function updateReply(
  state: CommunityForumState,
  replyId: string,
  body: string,
): CommunityForumState {
  const ts = nowIso();
  return withCounts({
    ...state,
    replies: state.replies.map((r) =>
      r.id === replyId ? { ...r, body: body.trim(), updatedAt: ts } : r,
    ),
  });
}

export function deleteReply(state: CommunityForumState, replyId: string): CommunityForumState {
  const reply = state.replies.find((r) => r.id === replyId);
  if (!reply) return state;
  return withCounts({
    ...state,
    replies: state.replies.filter((r) => r.id !== replyId),
    topics: state.topics.map((t) =>
      t.id === reply.topicId ? { ...t, replyCount: Math.max(0, t.replyCount - 1) } : t,
    ),
  });
}

export function getRepliesForTopic(state: CommunityForumState, topicId: string): TopicReply[] {
  return state.replies
    .filter((r) => r.topicId === topicId)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export function getPinnedTopics(state: CommunityForumState): CommunityTopic[] {
  return state.topics
    .filter((t) => t.status === 'pinned')
    .sort((a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime())
    .slice(0, 5);
}

export function getPopularTopics(state: CommunityForumState): CommunityTopic[] {
  return [...state.topics]
    .filter((t) => t.status !== 'hidden')
    .sort((a, b) => b.replyCount - a.replyCount)
    .slice(0, 5);
}

export function getRecentSummaries(state: CommunityForumState): CommunityTopic[] {
  return state.topics
    .filter((t) => t.hasSummary && t.summary && t.status !== 'hidden')
    .sort((a, b) =>
      new Date(b.summary!.updatedAt).getTime() - new Date(a.summary!.updatedAt).getTime(),
    )
    .slice(0, 4);
}
